import { useCallback } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import URLS from 'constants/urls';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { selectActiveVenue } from 'selectors/general';

import { fetchPartnerOrders } from './SkuOrders.api';
import { LIFECYCLE_STATUS, PARTNER_ORDERS_TAB } from './SkuOrders.constants';
import { skuOrdersKeys } from './skuOrdersKeys';

export function usePartnerOrders(activeTab, options = {}) {
  const { venueId, timezone: venueTimezone, venueName } = useSelector(selectActiveVenue);

  return useQuery({
    queryKey: skuOrdersKeys.list(venueId, activeTab),
    queryFn: () => fetchPartnerOrders({ venueId, tab: activeTab, venueTimezone, venueName }),
    enabled: !!venueId,
    ...options,
  });
}

export function useInvalidatePartnerOrders() {
  const queryClient = useQueryClient();
  return useCallback(() => queryClient.invalidateQueries({ queryKey: skuOrdersKeys.all }), [queryClient]);
}

// CancelOrder is async server-side; patch the react-query cache immediately after a 200 from cancelOrder.
export function applyOptimisticOrderCancelled(queryClient, { venueId, orderId, cancellationType }) {
  if (!venueId || !orderId || !cancellationType) return;

  const cancelledAt = new Date().toISOString();

  const updater = oldData => {
    if (!oldData?.orders?.length) return oldData;

    return {
      ...oldData,
      orders: oldData.orders.map(order => {
        if (order?.core?.orderId !== orderId) return order;

        return {
          ...order,
          lifecycleStatus: LIFECYCLE_STATUS.CANCELLED,
          orderItems: (order.orderItems || []).map(item => ({
            ...item,
            core: {
              ...item.core,
              cancelledAt,
              cancellationType,
            },
          })),
        };
      }),
    };
  };

  [PARTNER_ORDERS_TAB.TODAY, PARTNER_ORDERS_TAB.HISTORY].forEach(tab => {
    queryClient.setQueryData(skuOrdersKeys.list(venueId, tab), updater);
  });
}

export function useApplyOptimisticOrderCancelled() {
  const queryClient = useQueryClient();
  const { venueId } = useSelector(selectActiveVenue);

  return useCallback(
    (orderId, cancellationType) => applyOptimisticOrderCancelled(queryClient, { venueId, orderId, cancellationType }),
    [queryClient, venueId]
  );
}

export function useFulfillSkuOrder(options = {}) {
  return useMutation({
    mutationFn: async orderId => {
      try {
        const url = URLS.skuOrders.fulfillOrder(orderId);
        const response = await httpClient.post(url, {}, { ignoreInternalError: true });
        return response?.data ?? null;
      } catch (error) {
        reportError(error);
        throw error;
      }
    },
    ...options,
  });
}

export function useCancelSkuOrder(options = {}) {
  return useMutation({
    mutationFn: async orderId => {
      try {
        const url = URLS.skuOrders.cancelOrder(orderId);
        const response = await httpClient.post(url, undefined, { ignoreInternalError: true });
        return response?.data ?? null;
      } catch (error) {
        reportError(error);
        throw error;
      }
    },
    ...options,
  });
}
